import React from 'react'
import { featureFlags } from '../config/featureFlags'
import {
  fetchExecutionContext,
  fetchExecutionSessions,
  getPersistedExecutionStandard,
  setPersistedExecutionStandard,
  type ExecutionContext,
} from '../services/complianceExecutionApi'

type SessionSummary = {
  standardCode: string
  currentStage?: string
  openBlockerCount?: number
}

type ComplianceExecutionContextValue = {
  enabled: boolean
  loading: boolean
  selectedStandard: string | null
  setSelectedStandard: (code: string | null) => void
  sessions: SessionSummary[]
  enabledStandards: string[]
  context: ExecutionContext | null
  refreshContext: (standardCode?: string) => Promise<void>
  refreshSessions: () => Promise<void>
}

const ComplianceExecutionContext = React.createContext<ComplianceExecutionContextValue | null>(null)

export function ComplianceExecutionProvider({ children }: { children: React.ReactNode }) {
  const enabled = featureFlags.COMPLIANCE_EXECUTION_ORCHESTRATION_V1
  const [loading, setLoading] = React.useState(false)
  const [selectedStandard, setSelectedStandardState] = React.useState<string | null>(() =>
    getPersistedExecutionStandard(),
  )
  const [sessions, setSessions] = React.useState<SessionSummary[]>([])
  const [enabledStandards, setEnabledStandards] = React.useState<string[]>([])
  const [context, setContext] = React.useState<ExecutionContext | null>(null)

  const setSelectedStandard = React.useCallback((code: string | null) => {
    setSelectedStandardState(code)
    setPersistedExecutionStandard(code)
  }, [])

  const refreshSessions = React.useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const data = await fetchExecutionSessions()
      setSessions(data.sessions || [])
      const codes = data.enabledStandards || data.sessions?.map((s) => s.standardCode) || []
      setEnabledStandards(codes)
      if (codes.length && !selectedStandard) {
        setSelectedStandardState(codes.includes('SOC2') ? 'SOC2' : codes[0])
      }
      if (!selectedStandard && data.enabledStandards?.[0]) {
        setSelectedStandard(data.enabledStandards[0])
      }
    } catch {
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [enabled, selectedStandard, setSelectedStandard])

  const refreshContext = React.useCallback(
    async (standardCode?: string) => {
      const code = standardCode || selectedStandard
      if (!enabled || !code) {
        setContext(null)
        return
      }
      setLoading(true)
      try {
        const ctx = await fetchExecutionContext(code)
        setContext(ctx)
      } catch {
        setContext(null)
      } finally {
        setLoading(false)
      }
    },
    [enabled, selectedStandard],
  )

  React.useEffect(() => {
    void refreshSessions()
  }, [refreshSessions])

  React.useEffect(() => {
    if (selectedStandard) void refreshContext(selectedStandard)
  }, [selectedStandard, refreshContext])

  const value = React.useMemo(
    () => ({
      enabled,
      loading,
      selectedStandard,
      setSelectedStandard,
      sessions,
      enabledStandards,
      context,
      refreshContext,
      refreshSessions,
    }),
    [enabled, loading, selectedStandard, setSelectedStandard, sessions, enabledStandards, context, refreshContext, refreshSessions],
  )

  return (
    <ComplianceExecutionContext.Provider value={value}>{children}</ComplianceExecutionContext.Provider>
  )
}

export function useComplianceExecution() {
  const ctx = React.useContext(ComplianceExecutionContext)
  return (
    ctx || {
      enabled: false,
      loading: false,
      selectedStandard: null,
      setSelectedStandard: () => {},
      sessions: [],
      enabledStandards: [],
      context: null,
      refreshContext: async () => {},
      refreshSessions: async () => {},
    }
  )
}
